import { useMutation } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import InputDocuments from '@components/InputDocuments';
import { ADD_FILE, ADD_FILE_FOR_TALENT_UPERCUT, DELETE_FILES, DELETE_FILES_FOR_TALENT_UPERCUT } from '@gql/file';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { talentTypeOfDocuments } from './constants/documents';

type Props = {
    documents?: any;
    documentType: string;
    talentId?: string;
    onCancel?: () => void;
    onSubmitted?: () => void;
    isEdit?: boolean;
};

const TalentDocuments = ({ documents, documentType, talentId, onCancel, onSubmitted, ...props }: Props) => {
    const [loading, setLoading] = useState(false);
    const typeOfDocument = talentTypeOfDocuments.find(item => item.type === documentType);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isDirty },
    } = useForm({
        mode: 'all',
        defaultValues: {
            [documentType]: documents,
        },
    });
    const [uploadedFiles, setUploadedFiles] = useState(documents);
    const [deleteFiles] = useMutation(talentId ? DELETE_FILES_FOR_TALENT_UPERCUT : DELETE_FILES);

    const [save] = useMutation(talentId ? ADD_FILE_FOR_TALENT_UPERCUT : ADD_FILE);

    const submitHandler = useCallback(
        async documentInput => {
            setLoading(true);
            const prevData = [...documentInput[documentType]];
            // check if there's any file to delete
            const filesToDelete = uploadedFiles
                ?.filter(({ _id }) => !!_id)
                ?.filter(({ _id }) => !documentInput[documentType]?.find(({ _id: id }) => id === _id));

            if (filesToDelete?.length) {
                await deleteFiles({
                    variables: {
                        _ids: filesToDelete.map(({ _id }) => _id),
                        ...(talentId ? { talentId } : {}),
                    },
                });
                prevData.filter(({ _id }) => !filesToDelete.find(({ _id: id }) => id === _id));
            }

            // check if there's any file to add
            await Promise.all(
                documentInput[documentType].map(async (file, index) => {
                    if (!!file._id) return;
                    const uploadResult = await save({
                        variables: {
                            file: file,
                            context: documentType,
                            ...(talentId ? { talentId } : {}),
                        },
                        context: {
                            fetchOptions: {
                                useUpload: true,
                                onProgress: (ev: ProgressEvent) => {
                                    const prevData = [...documentInput[documentType]];
                                    prevData[index].uploadProgress = Math.round((ev.loaded / ev.total) * 100);
                                    setValue(documentType, prevData);
                                },
                            },
                        },
                    });
                    prevData[index] = talentId ? uploadResult.data?.addFileForTalentUpercut : uploadResult.data?.addFile;
                }),
            );

            onSubmitted?.();
            setLoading(false);
            setUploadedFiles(prevData);
            reset({ [documentType]: prevData });
        },
        [deleteFiles, documentType, onSubmitted, reset, save, setValue, talentId, uploadedFiles],
    );

    return (
        <FormContainer
            style={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}
            onBack={() => { }}
            loading={loading}
            formState={!isDirty ? undefined : 'edit'}
            onCancel={() => {
                reset({ [documentType]: documents });
                onCancel?.();
            }}
            onSubmit={handleSubmit(submitHandler)}
            submitText={props.isEdit ? 'Enregistrer' : 'Suivant'}
            formTitle={typeOfDocument?.name}
            formDescription={typeOfDocument?.description}
        >
            {documentType && (
                <InputDocuments
                    name={documentType}
                    control={control}
                    label={typeOfDocument?.label}
                    helpText={typeOfDocument?.helpText}
                    placeholder="Files"
                    defaultValue={''}
                    multiple
                    maxFiles={typeOfDocument?.maxFiles}
                    disabled={loading}
                    files={uploadedFiles}
                    showErrorMessage
                />
            )}
        </FormContainer>
    );
};

export default TalentDocuments;
